import { useState, useMemo } from 'react'
import { chartBarGradient } from '@/config/chartTheme'
import {
  Search,
  Newspaper,
  Bell,
  BellOff,
  Plus,
  X,
  Bookmark,
  Share2,
  ExternalLink,
  Clock,
  Tag,
  CircleDot,
  TrendingUp,
  Building2,
  FileText,
  Shield,
  Briefcase,
  BarChart3,
} from 'lucide-react'
import clsx from 'clsx'
import BlankSlate from '@/components/shared/BlankSlate'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NewsCategory = 'All' | 'Breaking' | 'CRE' | 'CMBS' | 'Rates' | 'Deals' | 'Regulatory'

interface NewsArticle {
  id: string
  title: string
  summary: string
  body: string
  source: string
  category: Exclude<NewsCategory, 'All'>
  timestamp: string
  isBreaking: boolean
  tags: string[]
  author?: string
  imageUrl?: string
}

interface TrackingAlert {
  id: string
  entity: string
  type: 'Company' | 'Person' | 'Keyword' | 'Property'
  mentionsThisWeek: number
  lastMention: string
  isActive: boolean
}

// ---------------------------------------------------------------------------
// Category config
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CATEGORIES: { id: NewsCategory; label: string; icon: React.ComponentType<any> }[] = [
  { id: 'All', label: 'All', icon: Newspaper },
  { id: 'Breaking', label: 'Breaking', icon: CircleDot },
  { id: 'CRE', label: 'CRE', icon: Building2 },
  { id: 'CMBS', label: 'CMBS', icon: BarChart3 },
  { id: 'Rates', label: 'Rates', icon: TrendingUp },
  { id: 'Deals', label: 'Deals', icon: Briefcase },
  { id: 'Regulatory', label: 'Regulatory', icon: Shield },
]

// ---------------------------------------------------------------------------
// Helper: relative time
// ---------------------------------------------------------------------------

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// ---------------------------------------------------------------------------
// Articles and tracking alerts
// ---------------------------------------------------------------------------

// There is no market-news provider, so there are no articles. This page used
// to carry about forty sample stories written for sales demos and attributed
// to real publications and named reporters, one of them a story about the
// client itself. Invented reporting under a real outlet's or a real person's
// name, or about a real firm, is a fabricated record whether or not a demo
// switch is on, so the set was removed rather than relabelled. Headlines reach
// this page only from a real feed; until one exists the page says so.
// (marketNewsFabrication.guard.test.ts keeps it that way.)
const now = Date.now()
const ARTICLES: NewsArticle[] = []
const INITIAL_ALERTS: TrackingAlert[] = []

// ---------------------------------------------------------------------------
// Category badge colors
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  Breaking: 'bg-red-500/20 text-red-400 border-red-400/20',
  CRE: 'bg-amber-400/10 text-amber-300 border-amber-400/20',
  CMBS: 'bg-sky-400/10 text-sky-300 border-sky-400/20',
  Rates: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20',
  Deals: 'bg-violet-400/10 text-violet-300 border-violet-400/20',
  Regulatory: 'bg-orange-400/10 text-orange-300 border-orange-400/20',
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MarketNews() {
  const [activeCategory, setActiveCategory] = useState<NewsCategory>('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(ARTICLES[0]?.id ?? null)
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null)
  const [readArticles, setReadArticles] = useState<Set<string>>(new Set())
  const [alerts, setAlerts] = useState<TrackingAlert[]>(INITIAL_ALERTS)
  const [showAddAlert, setShowAddAlert] = useState(false)
  const [newAlertName, setNewAlertName] = useState('')
  const [savedArticles, setSavedArticles] = useState<Set<string>>(new Set())

  // Filtered articles
  const filteredArticles = useMemo(() => {
    let arts = ARTICLES
    if (activeCategory !== 'All') {
      arts = arts.filter((a) => a.category === activeCategory)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      arts = arts.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      )
    }
    return arts
  }, [activeCategory, searchQuery])

  const selectedArticle = selectedAlertId
    ? null
    : ARTICLES.find((a) => a.id === selectedArticleId) ?? null

  const selectedAlert = alerts.find((a) => a.id === selectedAlertId) ?? null

  const alertArticles = useMemo(() => {
    if (!selectedAlert) return []
    const entity = selectedAlert.entity.toLowerCase()
    return ARTICLES.filter((a) =>
      a.tags.some((t) => t.toLowerCase().includes(entity)) ||
      a.title.toLowerCase().includes(entity) ||
      a.body.toLowerCase().includes(entity)
    )
  }, [selectedAlert])

  function handleSelectArticle(id: string) {
    setSelectedArticleId(id)
    setSelectedAlertId(null)
    setReadArticles((prev) => new Set(prev).add(id))
  }

  function handleSelectAlert(id: string) {
    setSelectedAlertId(id === selectedAlertId ? null : id)
    setSelectedArticleId(null)
  }

  function toggleAlertActive(id: string) {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, isActive: !a.isActive } : a)))
  }

  function addAlert() {
    if (!newAlertName.trim()) return
    const newAlert: TrackingAlert = {
      id: `ta-${Date.now()}`,
      entity: newAlertName.trim(),
      type: 'Keyword',
      mentionsThisWeek: 0,
      lastMention: new Date().toISOString(),
      isActive: true,
    }
    setAlerts((prev) => [...prev, newAlert])
    setNewAlertName('')
    setShowAddAlert(false)
  }

  function toggleSaveArticle(id: string) {
    setSavedArticles((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function trackEntityFromTag(tag: string) {
    if (alerts.some((a) => a.entity.toLowerCase() === tag.toLowerCase())) return
    const newAlert: TrackingAlert = {
      id: `ta-${Date.now()}`,
      entity: tag,
      type: 'Keyword',
      mentionsThisWeek: ARTICLES.filter((a) =>
        a.tags.some((t) => t.toLowerCase() === tag.toLowerCase())
      ).length,
      lastMention: new Date().toISOString(),
      isActive: true,
    }
    setAlerts((prev) => [...prev, newAlert])
  }

  const TYPE_BADGE: Record<string, string> = {
    Company: 'bg-violet-400/10 text-violet-300 border-violet-400/20',
    Person: 'bg-sky-400/10 text-sky-300 border-sky-400/20',
    Keyword: 'bg-amber-400/10 text-amber-300 border-amber-400/20',
    Property: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20',
  }

  if (ARTICLES.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-4 py-3">
        {/* MOD-30: this used to invite the user to "Connect an integration"
            for a feed the platform does not offer — Settings → Integrations
            has no market-news provider (Serper there feeds CRM contact news
            only). Say what is true instead. */}
        <BlankSlate
          icon={Newspaper}
          title="Market news is not available yet"
          description="The platform has no market-news feed: a news provider for CRE, CMBS, rates and regulatory headlines is not built or signed off, so there is nothing to connect. News about a specific contact or firm is on its CRM record."
        />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden p-3 gap-3">
      {/* ── Top bar: category tabs + search ── */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
        {/* Category tabs */}
        <div className="flex items-center gap-1">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={clsx(
                  'flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-all duration-150',
                  activeCategory === cat.id
                    ? 'border-amber-400/25 bg-amber-400/[0.08] text-amber-300'
                    : 'border-transparent text-stone-400 hover:border-amber-400/10 hover:bg-white/[0.02] hover:text-stone-200'
                )}
              >
                <Icon size={12} />
                {cat.label}
              </button>
            )
          })}
        </div>

        {/* Search */}
        <div className="ml-auto flex items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1 min-w-[220px] max-w-sm flex-1">
          <Search size={12} className="text-stone-600 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles..."
            className="w-full bg-transparent text-[11px] text-stone-300 placeholder:text-stone-600 outline-none"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-stone-600 hover:text-stone-300">
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="grid min-h-0 flex-1 grid-cols-[55fr_45fr] grid-rows-[1fr_auto] gap-3">
        {/* ── LEFT TOP: Live Feed ── */}
        <div className="rounded-lg border border-amber-400/10 bg-black/30 flex flex-col overflow-hidden min-h-0">
          <div className="panel-header flex-shrink-0">
            <span className="panel-header-title flex items-center gap-1.5">
              <CircleDot size={10} className="text-red-400 animate-pulse" />
              Live Feed
            </span>
            <span className="text-[10px] font-mono text-stone-600">{filteredArticles.length} articles</span>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {filteredArticles.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-stone-600 text-xs">No articles match your filters.</div>
            ) : (
              filteredArticles.map((article) => {
                const isRead = readArticles.has(article.id)
                const isSelected = selectedArticleId === article.id && !selectedAlertId
                return (
                  <button
                    key={article.id}
                    onClick={() => handleSelectArticle(article.id)}
                    className={clsx(
                      'w-full text-left px-3 py-2.5 border-l-2 transition-all duration-100',
                      isSelected
                        ? 'bg-amber-400/[0.06] border-l-amber-400/60'
                        : article.isBreaking
                        ? 'border-l-red-400/50 hover:bg-white/[0.02]'
                        : isRead
                        ? 'border-l-transparent hover:bg-white/[0.02]'
                        : 'border-l-amber-400/20 hover:bg-white/[0.02]',
                      'border-b border-b-amber-400/[0.06]'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {article.isBreaking && (
                        <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-red-400">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" />
                          Breaking
                        </span>
                      )}
                      <span className="text-[10px] text-stone-600 flex items-center gap-1">
                        <Clock size={9} />
                        {relativeTime(article.timestamp)}
                      </span>
                    </div>
                    <div className={clsx('text-[12px] font-semibold leading-snug mb-1', isRead ? 'text-stone-400' : 'text-stone-200')}>
                      {article.title}
                    </div>
                    <div className="text-[11px] text-stone-500 leading-relaxed line-clamp-2 mb-1.5">{article.summary}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-stone-500">
                        {article.source}
                      </span>
                      <span className={clsx('text-[9px] font-medium px-1.5 py-0.5 rounded border', CATEGORY_COLORS[article.category])}>
                        {article.category}
                      </span>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* ── RIGHT TOP: Article Detail / Entity Detail ── */}
        <div className="rounded-lg border border-amber-400/10 bg-black/30 flex flex-col overflow-hidden min-h-0">
          <div className="panel-header flex-shrink-0">
            <span className="panel-header-title">
              {selectedAlert ? 'Entity Intel' : 'Article Detail'}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
            {selectedAlert ? (
              /* ── Tracked Entity Detail ── */
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={clsx('text-[9px] font-medium px-1.5 py-0.5 rounded border', TYPE_BADGE[selectedAlert.type])}>
                    {selectedAlert.type}
                  </span>
                </div>
                <h2 className="text-lg font-bold text-stone-100 mb-1">{selectedAlert.entity}</h2>
                <div className="flex items-center gap-3 text-[11px] text-stone-500 mb-4">
                  <span>{selectedAlert.mentionsThisWeek} mentions this week</span>
                  <span>Last seen {relativeTime(selectedAlert.lastMention)}</span>
                </div>
                <div className="gold-divider mb-4" />
                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-2">
                  Recent Articles ({alertArticles.length})
                </div>
                <div className="space-y-2">
                  {alertArticles.length === 0 ? (
                    <div className="text-xs text-stone-600">No articles mention this entity.</div>
                  ) : (
                    alertArticles.map((art) => (
                      <button
                        key={art.id}
                        onClick={() => handleSelectArticle(art.id)}
                        className="w-full text-left rounded-md border border-amber-400/[0.06] bg-black/20 px-3 py-2 hover:bg-amber-400/[0.04] transition-all"
                      >
                        <div className="text-[11px] font-medium text-stone-300 leading-snug mb-0.5">{art.title}</div>
                        <div className="flex items-center gap-2 text-[10px] text-stone-600">
                          <span>{art.source}</span>
                          <span>{relativeTime(art.timestamp)}</span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : selectedArticle ? (
              /* ── Article Body ── */
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {selectedArticle.isBreaking && (
                    <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-red-400 bg-red-400/10 px-2 py-0.5 rounded border border-red-400/20">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" />
                      Breaking
                    </span>
                  )}
                  <span className={clsx('text-[9px] font-medium px-1.5 py-0.5 rounded border', CATEGORY_COLORS[selectedArticle.category])}>
                    {selectedArticle.category}
                  </span>
                </div>

                <h2 className="text-[16px] font-bold text-stone-100 leading-tight mb-2">{selectedArticle.title}</h2>

                <div className="flex items-center gap-3 text-[11px] text-stone-500 mb-4 flex-wrap">
                  <span className="font-medium text-stone-400">{selectedArticle.source}</span>
                  {selectedArticle.author && (
                    <span>by {selectedArticle.author}</span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock size={10} />
                    {relativeTime(selectedArticle.timestamp)}
                  </span>
                </div>

                <div className="gold-divider mb-4" />

                {/* Body text */}
                <div className="space-y-3 mb-6">
                  {selectedArticle.body.split('\n\n').map((para, i) => (
                    <p key={i} className="text-[12px] leading-relaxed text-stone-300">{para}</p>
                  ))}
                </div>

                <div className="gold-divider mb-4" />

                {/* Tags */}
                <div className="mb-4">
                  <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-2 flex items-center gap-1">
                    <Tag size={10} />
                    Entities & Tags
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedArticle.tags.map((tag) => {
                      const isTracked = alerts.some((a) => a.entity.toLowerCase() === tag.toLowerCase())
                      return (
                        <button
                          key={tag}
                          onClick={() => trackEntityFromTag(tag)}
                          className={clsx(
                            'text-[10px] px-2 py-0.5 rounded border transition-all',
                            isTracked
                              ? 'border-amber-400/30 bg-amber-400/10 text-amber-300'
                              : 'border-amber-400/10 bg-black/20 text-stone-400 hover:text-amber-300 hover:border-amber-400/20'
                          )}
                          title={isTracked ? 'Already tracking' : `Track "${tag}"`}
                        >
                          {tag}
                          {!isTracked && <Plus size={8} className="inline ml-1 -mt-0.5" />}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleSaveArticle(selectedArticle.id)}
                    className={clsx(
                      'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[11px] font-medium transition-all',
                      savedArticles.has(selectedArticle.id)
                        ? 'border-amber-400/30 bg-amber-400/10 text-amber-300'
                        : 'border-amber-400/10 bg-black/30 text-stone-400 hover:text-stone-200 hover:border-amber-400/20'
                    )}
                  >
                    <Bookmark size={12} />
                    {savedArticles.has(selectedArticle.id) ? 'Saved' : 'Save to CRM'}
                  </button>
                  <button className="flex items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/30 px-3 py-1.5 text-[11px] font-medium text-stone-400 hover:text-stone-200 hover:border-amber-400/20 transition-all">
                    <Share2 size={12} />
                    Share
                  </button>
                  <button className="flex items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/30 px-3 py-1.5 text-[11px] font-medium text-stone-400 hover:text-stone-200 hover:border-amber-400/20 transition-all">
                    <ExternalLink size={12} />
                    Source
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-stone-600 text-xs">Select an article or tracked entity to view details.</div>
            )}
          </div>
        </div>

        {/* ── LEFT BOTTOM: Tracking Alerts ── */}
        <div className="rounded-lg border border-amber-400/10 bg-black/30 flex flex-col overflow-hidden max-h-[240px]">
          <div className="panel-header flex-shrink-0">
            <span className="panel-header-title flex items-center gap-1.5">
              <Bell size={10} className="text-amber-300" />
              Tracking Alerts
            </span>
            <button
              onClick={() => setShowAddAlert(!showAddAlert)}
              className="flex items-center gap-1 text-[10px] text-amber-300/80 hover:text-amber-300 transition-colors"
            >
              <Plus size={10} />
              Add
            </button>
          </div>

          {showAddAlert && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-400/[0.06] bg-black/20">
              <input
                type="text"
                value={newAlertName}
                onChange={(e) => setNewAlertName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addAlert()}
                placeholder="Entity name..."
                className="flex-1 bg-transparent text-[11px] text-stone-300 placeholder:text-stone-600 outline-none border-b border-amber-400/20 py-1"
                autoFocus
              />
              <button onClick={addAlert} className="text-[10px] text-amber-300 hover:text-amber-200 font-medium">
                Track
              </button>
              <button onClick={() => { setShowAddAlert(false); setNewAlertName('') }} className="text-stone-600 hover:text-stone-400">
                <X size={12} />
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {alerts.map((alert) => (
              <button
                key={alert.id}
                onClick={() => handleSelectAlert(alert.id)}
                className={clsx(
                  'w-full text-left flex items-center gap-2 px-3 py-2 border-b border-amber-400/[0.06] transition-all',
                  selectedAlertId === alert.id
                    ? 'bg-amber-400/[0.06]'
                    : 'hover:bg-white/[0.02]'
                )}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); toggleAlertActive(alert.id) }}
                  className="flex-shrink-0"
                  title={alert.isActive ? 'Mute alert' : 'Activate alert'}
                >
                  {alert.isActive ? (
                    <Bell size={12} className="text-amber-300/80" />
                  ) : (
                    <BellOff size={12} className="text-stone-600" />
                  )}
                </button>
                <span className={clsx('text-[11px] font-medium flex-1', alert.isActive ? 'text-stone-200' : 'text-stone-500')}>
                  {alert.entity}
                </span>
                <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border', TYPE_BADGE[alert.type])}>
                  {alert.type}
                </span>
                <span className="text-[10px] text-stone-500 font-mono w-[80px] text-right">
                  {alert.mentionsThisWeek} /wk
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── RIGHT BOTTOM: Quick stats / Tracked Entities summary ── */}
        <div className="rounded-lg border border-amber-400/10 bg-black/30 flex flex-col overflow-hidden max-h-[240px]">
          <div className="panel-header flex-shrink-0">
            <span className="panel-header-title flex items-center gap-1.5">
              <FileText size={10} className="text-amber-300" />
              Market Pulse
            </span>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin p-3">
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="metric-cell">
                <div className="metric-cell-label">Articles Today</div>
                <div className="metric-cell-value text-stone-100">
                  {ARTICLES.filter((a) => (now - new Date(a.timestamp).getTime()) < 86_400_000).length}
                </div>
              </div>
              <div className="metric-cell">
                <div className="metric-cell-label">Breaking</div>
                <div className="metric-cell-value text-red-400">
                  {ARTICLES.filter((a) => a.isBreaking).length}
                </div>
              </div>
              <div className="metric-cell">
                <div className="metric-cell-label">Tracked</div>
                <div className="metric-cell-value text-amber-300">
                  {alerts.filter((a) => a.isActive).length}
                </div>
              </div>
            </div>

            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-2">Top Sources</div>
            <div className="space-y-1">
              {(() => {
                const counts: Record<string, number> = {}
                ARTICLES.forEach((a) => { counts[a.source] = (counts[a.source] || 0) + 1 })
                return Object.entries(counts)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between text-[11px]">
                      <span className="text-stone-400">{source}</span>
                      <div className="flex items-center gap-2">
                        <div className="chart-viz-track chart-viz-bar--rounded h-1 w-20">
                          <div
                            className="chart-viz-bar chart-viz-bar--rounded h-full"
                            style={{
                              width: `${(count / ARTICLES.length) * 100}%`,
                              background: chartBarGradient(1, 2),
                            }}
                          />
                        </div>
                        <span className="text-stone-600 font-mono text-[10px] w-4 text-right">{count}</span>
                      </div>
                    </div>
                  ))
              })()}
            </div>

            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-2 mt-3">Category Mix</div>
            <div className="flex flex-wrap gap-1.5">
              {(['CRE', 'CMBS', 'Rates', 'Deals', 'Regulatory'] as const).map((cat) => {
                const count = ARTICLES.filter((a) => a.category === cat).length
                return (
                  <span key={cat} className={clsx('text-[9px] font-medium px-2 py-0.5 rounded border', CATEGORY_COLORS[cat])}>
                    {cat} {count}
                  </span>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
